const CART_DRAWER_MODAL_FALLBACK_MS = 700;
const CART_DRAWER_SECTION_ID = 'cart-drawer';
// Parts of the drawer swapped in place after a cart change that does not go
// through cart.js' own quantity flow (add-to-cart from a product form,
// discount code applied, etc.).
const CART_DRAWER_REFRESH_SELECTORS = ['.drawer__header', 'cart-drawer-items', '.cart-drawer__footer'];

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeModal = null;
    this.modalTrigger = null;

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    // Delegated so listeners survive the innerHTML swaps done by renderContents.
    this.addEventListener('click', this.onClick.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  onKeyUp(event) {
    if (event.code !== 'Escape') return;
    // A stale reference (modal markup swapped by a re-render) falls through to closing the drawer.
    if (this.activeModal?.isConnected) {
      this.closeModal();
      return;
    }
    this.close();
  }

  onClick(event) {
    if (event.target.closest('#CartDrawer-Overlay')) {
      this.close();
      return;
    }

    const opener = event.target.closest('[data-cart-drawer-modal]');
    if (opener) {
      event.preventDefault();
      this.openModal(opener.dataset.cartDrawerModal, opener);
      return;
    }

    if (event.target.closest('[data-cart-drawer-modal-close]')) {
      event.preventDefault();
      this.closeModal();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.closeModal(true);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  openModal(id, trigger) {
    const modal = this.querySelector(`#${id}`);
    if (!modal) return;
    if (this.activeModal && this.activeModal !== modal) this.closeModal(true);

    modal.hidden = false;
    // Force a style flush so the slide-up transition runs from the hidden state.
    modal.getBoundingClientRect();
    modal.classList.add('is-open');
    trigger?.setAttribute('aria-expanded', 'true');

    this.activeModal = modal;
    this.modalTrigger = trigger || null;

    // Prefer the form field; the header close button comes first in DOM order.
    const focusTarget = modal.querySelector('textarea, input') || modal.querySelector('button');
    focusTarget?.focus();
  }

  closeModal(immediate = false) {
    const modal = this.activeModal;
    if (!modal) return;

    const trigger = this.modalTrigger;
    this.activeModal = null;
    this.modalTrigger = null;

    modal.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');

    if (immediate || prefersReducedMotion()) {
      modal.hidden = true;
    } else {
      const container = modal.querySelector('.cart-drawer__modal-container');
      let hidden = false;
      const hide = () => {
        if (hidden) return;
        hidden = true;
        modal.hidden = true;
      };
      container?.addEventListener('transitionend', hide, { once: true });
      setTimeout(hide, CART_DRAWER_MODAL_FALLBACK_MS);
    }

    if (trigger?.isConnected) trigger.focus();
  }

  renderContents(parsedState) {
    this.activeModal = null;
    this.modalTrigger = null;
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

/**
 * Replaces the refreshable parts of the drawer with the ones found in a
 * freshly rendered `cart-drawer` section.
 * @param {string} sectionHtml
 * @param {string[]} [extraSelectors] selectors whose innerHTML is copied instead of replaced
 */
function refreshCartDrawerParts(sectionHtml, extraSelectors = []) {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  const html = new DOMParser().parseFromString(sectionHtml, 'text/html');

  // Read state before replaceWith() moves nodes out of the parsed document.
  const itemCount = html.querySelector('cart-drawer .drawer__count');
  const isEmpty = !itemCount || itemCount.hasAttribute('hidden');

  CART_DRAWER_REFRESH_SELECTORS.forEach((selector) => {
    const target = drawer.querySelector(selector);
    const source = html.querySelector(`cart-drawer ${selector}`);
    if (target && source) target.replaceWith(source);
  });

  extraSelectors.forEach((selector) => {
    const target = drawer.querySelector(selector);
    const source = html.querySelector(`cart-drawer ${selector}`);
    if (target && source) target.innerHTML = source.innerHTML;
  });

  drawer.classList.toggle('is-empty', isEmpty);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: CART_DRAWER_SECTION_ID,
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  onCartUpdate() {
    return fetch(`${routes.cart_url}?section_id=${CART_DRAWER_SECTION_ID}`)
      .then((response) => response.text())
      .then((responseText) => refreshCartDrawerParts(responseText))
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/**
 * Discount code form inside the cart drawer. Applies / removes codes through
 * POST /cart/update.js `discount` and refreshes the drawer from the bundled
 * section render.
 */
class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="discount"]');
    this.errorElement = this.querySelector('.cart-discount__error');

    this.form?.addEventListener('submit', this.onSubmit.bind(this));
    this.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-discount-remove]');
      if (!removeButton) return;
      event.preventDefault();
      this.removeCode(removeButton.dataset.discountRemove);
    });
  }

  get appliedCodes() {
    return [...this.querySelectorAll('[data-discount-code]')].map((element) => element.dataset.discountCode);
  }

  onSubmit(event) {
    event.preventDefault();
    const code = this.input?.value.trim();
    if (!code) return;
    if (this.appliedCodes.some((applied) => applied.toLowerCase() === code.toLowerCase())) {
      this.input.value = '';
      return;
    }
    this.applyCodes([...this.appliedCodes, code], code);
  }

  removeCode(code) {
    this.applyCodes(this.appliedCodes.filter((applied) => applied !== code));
  }

  async applyCodes(codes, newCode = null) {
    this.setLoading(true);
    this.showError('');

    try {
      let cart = await this.updateCart(codes);
      const rejected = newCode ? CartDiscount.findRejectedCode(cart, newCode) : null;

      if (rejected) {
        // Shopify keeps a non-applicable code on the cart; drop it again so it
        // does not linger and confuse the checkout.
        cart = await this.updateCart(codes.filter((code) => code !== newCode));
      }

      refreshCartDrawerParts(cart.sections?.[CART_DRAWER_SECTION_ID] || '', ['.cart-discount__codes']);

      if (rejected) {
        this.showError(window.cartStrings?.discountError);
      } else if (this.input) {
        this.input.value = '';
      }
    } catch (error) {
      console.error(error);
      this.showError(window.cartStrings?.discountErrorGeneric);
    } finally {
      this.setLoading(false);
    }
  }

  static findRejectedCode(cart, code) {
    const discountCodes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
    return discountCodes.find(
      (discount) => discount.code?.toLowerCase() === code.toLowerCase() && discount.applicable === false
    );
  }

  updateCart(codes) {
    const body = JSON.stringify({
      discount: codes.join(','),
      sections: [CART_DRAWER_SECTION_ID],
      sections_url: window.location.pathname,
    });

    return fetch(routes.cart_update_url, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((cart) => {
        if (!cart || cart.errors || cart.status) {
          throw new Error(cart?.description || cart?.errors || 'Cart update failed');
        }
        return cart;
      });
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    this.form?.querySelectorAll('button, input').forEach((element) => {
      element.disabled = isLoading;
    });
  }

  showError(message) {
    if (!this.errorElement) return;
    const text = this.errorElement.querySelector('.cart-discount__error-text');
    if (text) text.textContent = message || '';
    this.errorElement.hidden = !message;
  }
}

customElements.define('cart-discount', CartDiscount);
